/**
 * The language standard as far as text can decide it: American English, and wording no
 * harder than B2 — LANG-01.
 *
 * British spelling blocks publishing because it is a yes or no. Everything else here
 * warns, because it needs an editor's judgment: a British word can be American in another
 * sense, and a long sentence can be the right one. Every issue carries the text that
 * fixes it where one exists, so the console can offer the fix rather than describe it.
 * Why these rules and not a reading-level score: `docs/decisions/language-standard.md`.
 */
import { SENTENCE_WORDS_MAX, sentenceSpans, wordsIn } from './coach-voice.js';
const or = (words) => words.join('|');
/** So "unfavourable" and "reorganise" are caught as well as the words they are built on. */
const PREFIX = '(?:un|re|de|dis|mis|non|pre|over|under)?';
const OUR_STEMS = [
    'col',
    'fav',
    'flav',
    'hon',
    'hum',
    'lab',
    'neighb',
    'behavi',
    'harb',
    'rum',
    'vap',
    'vig',
    'endeav',
    'sav',
    'arm',
    'clam',
    'rig',
    'splend',
    'val',
    'tum',
    'parl',
    'ferv',
    'ard',
    'od',
];
// The verbs whose British form is `-ise`. A list, not a suffix rule: advertise, exercise,
// compromise and enterprise are `-ise` in American English too.
const ISE_STEMS = [
    'organ',
    'recogn',
    'real',
    'priorit',
    'maxim',
    'minim',
    'optim',
    'apolog',
    'categor',
    'capital',
    'critic',
    'custom',
    'emphas',
    'standard',
    'summar',
    'util',
    'final',
    'special',
    'visual',
    'monet',
    'digit',
    'global',
    'local',
    'normal',
    'author',
    'character',
    'memor',
    'familiar',
    'mobil',
    'stabil',
    'subsid',
    'incentiv',
    'central',
    'decentral',
    'rational',
    'legitim',
    'public',
    'commercial',
    'industrial',
    'privat',
    'synchron',
    'symbol',
    'energ',
    'personal',
    'general',
    'harmon',
    'jeopard',
    'modern',
    'patron',
    'sympath',
    'theor',
    'fertil',
    'hospital',
    'neutral',
    'penal',
    'scrutin',
    'trivial',
    'human',
    'formal',
    'conceptual',
    'operational',
    'vandal',
    'colon',
    'econom',
    'minimal',
    'marginal',
    'agon',
    'antagon',
    'galvan',
    'polar',
    'computer',
    'sanit',
    'steril',
];
const DOUBLED_L_STEMS = [
    'travel',
    'cancel',
    'label',
    'model',
    'fuel',
    'signal',
    'counsel',
    'level',
    'total',
    'channel',
    'tunnel',
    'quarrel',
    'dial',
    'equal',
    'rival',
    'shovel',
    'funnel',
    'pedal',
    'panel',
    'marshal',
    'jewel',
    'libel',
    'marvel',
    'grovel',
    'enamel',
    'refuel',
];
const WORDS = {
    catalogue: 'catalog',
    catalogues: 'catalogs',
    catalogued: 'cataloged',
    programme: 'program',
    programmes: 'programs',
    cheque: 'check',
    cheques: 'checks',
    grey: 'gray',
    tyre: 'tire',
    tyres: 'tires',
    aluminium: 'aluminum',
    judgement: 'judgment',
    judgements: 'judgments',
    acknowledgement: 'acknowledgment',
    acknowledgements: 'acknowledgments',
    ageing: 'aging',
    enrol: 'enroll',
    enrols: 'enrolls',
    enrolment: 'enrollment',
    enrolments: 'enrollments',
    fulfil: 'fulfill',
    fulfils: 'fulfills',
    fulfilment: 'fulfillment',
    instalment: 'installment',
    instalments: 'installments',
    skilful: 'skillful',
    skilfully: 'skillfully',
    wilful: 'willful',
    manoeuvre: 'maneuver',
    manoeuvres: 'maneuvers',
    practise: 'practice',
    practises: 'practices',
    practised: 'practiced',
    practising: 'practicing',
    learnt: 'learned',
    spelt: 'spelled',
    plough: 'plow',
    mould: 'mold',
    storey: 'story',
    storeys: 'stories',
    draught: 'draft',
    sceptic: 'skeptic',
    sceptical: 'skeptical',
    scepticism: 'skepticism',
    jewellery: 'jewelry',
    kerb: 'curb',
    sulphur: 'sulfur',
    paediatric: 'pediatric',
    encyclopaedia: 'encyclopedia',
    anaesthetic: 'anesthetic',
    oestrogen: 'estrogen',
    foetus: 'fetus',
    pyjamas: 'pajamas',
    cosy: 'cozy',
    gaol: 'jail',
    moustache: 'mustache',
    speciality: 'specialty',
    specialities: 'specialties',
    artefact: 'artifact',
    artefacts: 'artifacts',
};
const PLAIN_WORDS = {
    utilize: 'use',
    utilizes: 'uses',
    utilized: 'used',
    utilizing: 'using',
    utilization: 'use',
    facilitate: 'help',
    facilitates: 'helps',
    facilitated: 'helped',
    facilitating: 'helping',
    commence: 'start',
    commences: 'starts',
    commenced: 'started',
    approximately: 'about',
    subsequently: 'later',
    demonstrate: 'show',
    demonstrates: 'shows',
    demonstrated: 'showed',
    numerous: 'many',
    endeavor: 'try',
    sufficient: 'enough',
    consequently: 'so',
    nevertheless: 'still',
    ascertain: 'find out',
    terminate: 'end',
    terminated: 'ended',
    henceforth: 'from now on',
    notwithstanding: 'despite',
    aforementioned: 'this',
    'in order to': 'to',
    'prior to': 'before',
    'in the event that': 'if',
    'with regard to': 'about',
    'due to the fact that': 'because',
    'at this point in time': 'now',
    'a multitude of': 'many',
};
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const group = (match, index) => match[index] ?? '';
const fromList = (list) => (match) => list[group(match, 0).toLowerCase()] ?? group(match, 0);
const RULES = [
    {
        rule: 'spelling',
        pattern: new RegExp(`\\b(${PREFIX}(?:${or(OUR_STEMS)}))our(s|ed|ing|ite|ites|able|ably|er|ers|ful|hood|hoods|al|ally|less|y)?\\b`, 'giu'),
        fix: (match) => `${group(match, 1)}or${group(match, 2)}`,
    },
    {
        rule: 'spelling',
        pattern: new RegExp(`\\b(${PREFIX}(?:${or(ISE_STEMS)}))is(e|es|ed|ing|er|ers|ation|ations|ational)\\b`, 'giu'),
        fix: (match) => `${group(match, 1)}iz${group(match, 2)}`,
    },
    {
        rule: 'spelling',
        // Not `analyses`, which is also the plural of analysis in American English.
        pattern: /\b(anal|paral|catal)ys(e|ed|ing|er|ers)\b/giu,
        fix: (match) => `${group(match, 1)}yz${group(match, 2)}`,
    },
    {
        rule: 'spelling',
        pattern: /\b((?:kilo|milli|centi)?(?:met|lit)|cent|theat|fib|calib|somb|spect|lust|meag)re(s|d)?\b/giu,
        fix: (match) => `${group(match, 1)}er${group(match, 2) === 'd' ? 'ed' : group(match, 2)}`,
    },
    {
        rule: 'spelling',
        pattern: /\b(defen|offen|licen|preten)ce(s)?\b/giu,
        fix: (match) => `${group(match, 1)}se${group(match, 2)}`,
    },
    {
        rule: 'spelling',
        pattern: new RegExp(`\\b(${or(DOUBLED_L_STEMS)})l(ed|ing|er|ers|or|ors|ous)\\b`, 'giu'),
        fix: (match) => `${group(match, 1)}${group(match, 2)}`,
    },
    {
        rule: 'spelling',
        pattern: new RegExp(`\\b(?:${or(Object.keys(WORDS))})\\b`, 'giu'),
        fix: fromList(WORDS),
    },
    { rule: 'vocabulary', pattern: /\bCV\b/gu, fix: () => 'résumé', cased: true },
    {
        rule: 'vocabulary',
        pattern: /\bCVs\b/gu,
        fix: () => 'résumés',
        cased: true,
    },
    {
        rule: 'vocabulary',
        pattern: /\bfortnightly\b/giu,
        fix: () => 'every two weeks',
    },
    {
        rule: 'vocabulary',
        pattern: /\b(?:a )?fortnight\b/giu,
        fix: () => 'two weeks',
    },
    {
        rule: 'vocabulary',
        // Only where it plainly means money in: "employee turnover" is American English.
        pattern: /\b(annual|yearly|total|group|sales|(?:company|firm)(?:['’]s)?) turnover\b/giu,
        fix: (match) => `${group(match, 1)} revenue`,
    },
    {
        rule: 'vocabulary',
        pattern: /\bturnover(?= of [£$€\d])/giu,
        fix: () => 'revenue',
    },
    {
        rule: 'vocabulary',
        pattern: /\bon holiday\b/giu,
        fix: () => 'on vacation',
    },
    {
        rule: 'vocabulary',
        pattern: /\bholiday (pay|allowance|entitlement)\b/giu,
        fix: (match) => `vacation ${group(match, 1)}`,
    },
    {
        rule: 'vocabulary',
        pattern: /\bannual leave\b/giu,
        fix: () => 'vacation time',
    },
    { rule: 'vocabulary', pattern: /\bmaths\b/giu, fix: () => 'math' },
    { rule: 'vocabulary', pattern: /\bwhilst\b/giu, fix: () => 'while' },
    { rule: 'vocabulary', pattern: /\bamongst\b/giu, fix: () => 'among' },
    { rule: 'vocabulary', pattern: /\blorry\b/giu, fix: () => 'truck' },
    { rule: 'vocabulary', pattern: /\blorries\b/giu, fix: () => 'trucks' },
    { rule: 'vocabulary', pattern: /\bpetrol\b/giu, fix: () => 'gas' },
    {
        rule: 'vocabulary',
        pattern: /\bmobile phone(s)?\b/giu,
        fix: (match) => `cell phone${group(match, 1)}`,
    },
    {
        rule: 'vocabulary',
        pattern: /\bpostcode(s)?\b/giu,
        fix: (match) => `ZIP code${group(match, 1)}`,
    },
    {
        rule: 'vocabulary',
        pattern: /\bat the weekend\b/giu,
        fix: () => 'on the weekend',
    },
    {
        rule: 'vocabulary',
        pattern: /\bcar park(s)?\b/giu,
        fix: (match) => `parking lot${group(match, 1)}`,
    },
    { rule: 'vocabulary', pattern: /\bfull stop\b/giu, fix: () => 'period' },
    {
        rule: 'vocabulary',
        pattern: /\bmade redundant\b/giu,
        fix: () => 'laid off',
    },
    {
        rule: 'vocabulary',
        pattern: /\btrade union(s)?\b/giu,
        fix: (match) => `labor union${group(match, 1)}`,
    },
    {
        rule: 'date',
        pattern: new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)? (${or(MONTHS)})(?:,? (\\d{4}))?\\b`, 'gu'),
        fix: (match) => `${group(match, 2)} ${group(match, 1)}${match[3] === undefined ? '' : `, ${match[3]}`}`,
        cased: true,
    },
    {
        rule: 'number',
        // A thousands point and a decimal comma. The currency is left alone: a case in
        // pounds stays in pounds, only the separators move.
        pattern: /\b\d{1,3}(?:\.\d{3})+,\d{1,2}\b/gu,
        fix: (match) => group(match, 0)
            .replaceAll('.', '\u0000')
            .replace(',', '.')
            .replaceAll('\u0000', ','),
        cased: true,
    },
    {
        rule: 'plain-word',
        pattern: new RegExp(`\\b(?:${or(Object.keys(PLAIN_WORDS).map((word) => word.replaceAll(' ', '\\s+')))})\\b`, 'giu'),
        fix: (match) => PLAIN_WORDS[group(match, 0).toLowerCase().replace(/\s+/gu, ' ')] ??
            group(match, 0),
    },
];
function messageFor(rule, found, fix) {
    switch (rule) {
        case 'spelling':
            return `Use American spelling, received "${found}", write "${fix}".`;
        case 'vocabulary':
            return `This reads as British English, received "${found}", an American reader says "${fix}".`;
        case 'date':
            return `Write the month first, received "${found}", write "${fix}".`;
        case 'number':
            return `Use a comma for thousands and a period for decimals, received "${found}", write "${fix}".`;
        case 'plain-word':
            return `A plainer word keeps this at B1, received "${found}", write "${fix}".`;
    }
}
/** Direct quotes are the source's words and a link is an address, so neither is corrected. */
function quoted(text) {
    return [...text.matchAll(/"[^"\n]*"|“[^”\n]*”|https?:\/\/\S+/gu)].map((match) => [match.index, match.index + match[0].length]);
}
/** Whether the word at `at` opens a sentence, where a capital says nothing about a name. */
function opensSentence(text, at) {
    const before = text.slice(0, at).replace(/[ \t*_("“‘'-]+$/u, '');
    return before === '' || /[.!?:\n]$/u.test(before);
}
function matchCase(found, fix) {
    const first = found.charAt(0);
    if (first === first.toLowerCase())
        return fix;
    if (found.length > 1 && found === found.toUpperCase())
        return fix.toUpperCase();
    return fix.charAt(0).toUpperCase() + fix.slice(1);
}
/**
 * The same sentence as two, split at the joint nearest its middle.
 *
 * Only at a semicolon or a comma before "and", "but" or "so", where the second half is a
 * sentence already. Anything else needs a writer, so the issue carries no fix.
 */
function split(sentence) {
    const joints = [...sentence.matchAll(/(;|, (?=(?:and|but|so) ))\s*(\S)/gu)];
    const middle = sentence.length / 2;
    const best = joints
        .filter((joint) => {
        const head = sentence.slice(0, joint.index);
        const tail = sentence.slice(joint.index + joint[0].length - 1);
        return wordsIn(head) >= 4 && wordsIn(tail) >= 4;
    })
        .sort((a, b) => Math.abs(a.index - middle) - Math.abs(b.index - middle))[0];
    if (best === undefined)
        return null;
    const next = group(best, 2);
    return `${sentence.slice(0, best.index)}. ${next.toUpperCase()}${sentence.slice(best.index + best[0].length)}`;
}
function longSentences(text) {
    return sentenceSpans(text).flatMap(({ at, sentence: found }) => {
        const words = wordsIn(found);
        if (words <= SENTENCE_WORDS_MAX)
            return [];
        const opening = found.slice(0, 40);
        return [
            {
                rule: 'long-sentence',
                blocks: false,
                at,
                found,
                fix: split(found),
                message: `A sentence over ${String(SENTENCE_WORDS_MAX)} words is too long to read in one go. Split it in two, received ${String(words)} words in "${opening}${found.length > opening.length ? '…' : ''}".`,
            },
        ];
    });
}
const overlaps = (a, b) => a.at < b.at + b.found.length && b.at < a.at + a.found.length;
/**
 * Everything the standard says about one piece of text, in reading order.
 *
 * A warning inside the span of a blocking issue is dropped until that is fixed: two fixes
 * for one word would contradict each other, and the blocking one comes first.
 */
export function languageIssues(text) {
    if (text.trim() === '')
        return [];
    const quotes = quoted(text);
    const inQuote = (at) => quotes.some(([start, end]) => at >= start && at < end);
    const words = RULES.flatMap(({ rule, pattern, fix, cased }) => [...text.matchAll(pattern)].flatMap((match) => {
        const found = match[0];
        const at = match.index;
        if (inQuote(at))
            return [];
        const first = found.slice(0, 1);
        const capital = first !== first.toLowerCase();
        if (cased !== true && capital && !opensSentence(text, at))
            return [];
        const fixed = cased === true ? fix(match) : matchCase(found, fix(match));
        return [
            {
                rule,
                blocks: rule === 'spelling',
                at,
                found,
                fix: fixed,
                message: messageFor(rule, found, fixed),
            },
        ];
    }));
    const all = [
        ...words,
        ...longSentences(text).filter((one) => !inQuote(one.at)),
    ];
    const blocking = all.filter((one) => one.blocks);
    return all
        .filter((one) => one.blocks ||
        one.rule === 'long-sentence' ||
        !blocking.some((block) => overlaps(block, one)))
        .sort((a, b) => a.at - b.at);
}
/**
 * The text with the chosen fixes made, from the end backwards so each offset still holds.
 *
 * A fix overlapping one already made is skipped, which is what a long sentence and a word
 * inside it do: the caller re-reads the text and offers what is still left.
 */
export function applyLanguageFixes(text, issues) {
    const made = [];
    let result = text;
    for (const one of [...issues].sort((a, b) => b.at - a.at)) {
        if (one.fix === null)
            continue;
        if (text.slice(one.at, one.at + one.found.length) !== one.found)
            continue;
        if (made.some((done) => overlaps(done, one)))
            continue;
        result =
            result.slice(0, one.at) +
                one.fix +
                result.slice(one.at + one.found.length);
        made.push(one);
    }
    return result;
}
//# sourceMappingURL=language.js.map